import React from 'react';
import { Nullable } from 'global';
import { useFormContext, useWatch } from 'react-hook-form';
import { ChargeAttachType } from 'modules/equipment/types';
import { ListingForm } from 'components/shared/TableListing/types';

import { BreachIndicator } from '../BreachIndicator';
import type { ChargeListingService } from '../../../../../lib';
import { QuoteCalcViewModel } from '../../../../../api/types';
import { InvoiceDescriptionCell } from './InvoiceDescriptionCell';
import { EquipmentDescriptionCell } from './EquipmentDescriptionCell';
import { quoteCalcMapper } from '../../../../../mappers/quoteCalcMapper';
import { QuoteChargeListingEntity, QuoteLineItemType } from '../../../../../types';

interface Props {
  rowIndex: number;
  invoiceId: string;
  priceCalc: Nullable<QuoteCalcViewModel>;
  chargeService: ChargeListingService;
  onRemoveRow: VoidFunction;
  onEditEquipment: (id: string, rowIndex: number) => void;
  onRemoveEquipment: (id: string, rowIndex: number) => void;
}

export const DescriptionCell = ({
  rowIndex,
  invoiceId,
  priceCalc,
  chargeService,
  onRemoveRow,
  onEditEquipment,
  onRemoveEquipment,
}: Props) => {
  const { control } = useFormContext<ListingForm<QuoteChargeListingEntity>>();
  const currentRow = useWatch({ control, name: `bodyFields.${rowIndex}` });

  if (!currentRow) return null;

  const { type, charge, equipment } = currentRow;

  const breach = quoteCalcMapper.findCalcRow(priceCalc, currentRow)?.breach ?? null;

  if (type === QuoteLineItemType.Equipment) {
    return (
      <EquipmentDescriptionCell
        equipment={equipment}
        renderBreachIndicator={<BreachIndicator breach={breach} />}
        onEditEquipment={id => onEditEquipment(id, rowIndex)}
        onRemoveEquipment={id => onRemoveEquipment(id, rowIndex)}
      />
    );
  }

  const scope = chargeService.resolveParentScope(currentRow, invoiceId) ?? {
    id: invoiceId,
    type: ChargeAttachType.Invoice,
  };

  return (
    <InvoiceDescriptionCell
      scope={scope}
      charge={charge!}
      rowIndex={rowIndex}
      onRemoveRow={onRemoveRow}
      chargeService={chargeService}
      renderBreachIndicator={<BreachIndicator breach={breach} />}
    />
  );
};
